import { Icon, SvgIconNames } from '@toggle/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { WebSearch } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { TrustedSourcesModal } from './TrustedSourcesModal';
import * as S from './WebSearchDropdown.styles';

interface DropdownOption {
  key: string;
  name: string;
  icon: SvgIconNames;
  description: string;
  value: {
    web_search: WebSearch;
  };
  isChecked: boolean;
  disabled: boolean;
}

export const WebSearchDropdown = () => {
  const { t } = useTranslation(['chat']);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { conversationId } = useReasonerParams();

  const { webSearch, activeConversation, setAnalysisMode, setWebSearch } =
    useChat(state => ({
      webSearch: state.webSearch,
      analysisMode: state.analysisMode,
      activeConversation: state.getActiveConversation(conversationId),
      setAnalysisMode: state.setAnalysisMode,
      setWebSearch: state.setWebSearch,
    }));
  const { trackAlfredWebSearchSelected } = usePosthogEvents();

  const options: DropdownOption[] = [
    {
      key: 'restricted',
      name: t('chat:trustedSources.restricted.title'),
      icon: 'GlobeLocked',
      description: t('chat:trustedSources.restricted.description'),
      value: {
        web_search: 'restricted',
      },
      isChecked: webSearch === 'restricted',
      disabled: false,
    },
    {
      key: 'block',
      name: t('chat:trustedSources.block.title'),
      icon: 'GlobeOff',
      description: t('chat:trustedSources.block.description'),
      value: {
        web_search: 'block',
      },
      isChecked: webSearch === 'block',
      disabled: false,
    },
  ];

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <S.OptionWrapper>
        {filterOption.icon && <Icon iconName={filterOption.icon} size={16} />}
        <S.Label>{filterOption.name}</S.Label>
      </S.OptionWrapper>
    );
  };

  const disableSelection =
    activeConversation?.tasks?.[0]?.analysis_mode === 'Deep';

  return (
    <>
      <S.StyledDropdownMenu
        triggerProps={{
          disabled: disableSelection,
        }}
        triggerNode={
          <div>
            <S.StyledButton
              variant="empty"
              iconName={
                options.find(option => option.value.web_search === webSearch)
                  ?.icon
              }
              size="small"
              data-testid="web-search-dropdown-trigger"
              disabled={disableSelection}
            />
          </div>
        }
        contentProps={{
          id: 'web-search-dropdown-content',
        }}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        {options.map(option => {
          return (
            <div key={option.key}>
              <Option
                option={option}
                isChecked={option.isChecked}
                indentedDescription={24}
                disabled={option.disabled}
                handleClick={() => {
                  setWebSearch(option.value.web_search);

                  if (option.value.web_search === 'block') {
                    setAnalysisMode('Deep');
                  }

                  const typeMap: Record<
                    WebSearch,
                    'general' | 'trusted' | 'none'
                  > = {
                    open: 'general',
                    restricted: 'trusted',
                    block: 'none',
                  };
                  trackAlfredWebSearchSelected(
                    POSTHOG_TRACKING_SOURCES.ALFRED,
                    typeMap[option.value.web_search]
                  );
                }}
                renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
              />
              {option.key === 'restricted' && (
                <S.StyledXsButton
                  variant="tertiary"
                  size="xsmall"
                  label={t('chat:trustedSources.viewTrustedSources')}
                  onClick={() => {
                    setDropdownOpen(false);
                    setModalOpen(true);
                  }}
                />
              )}
            </div>
          );
        })}
      </S.StyledDropdownMenu>
      {modalOpen && (
        <TrustedSourcesModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
